import { Group, LoadingOverlay, Modal, Stack } from '@mantine/core';
import { useApi } from 'api/api-context';
import BrainItMoreInfo from 'components/BrainItMoreInfo';
import Button from 'components/Button';
import KepMaintenanceNotice from 'components/modals/kep-maintenance/KepMaintenanceNotice';
import P1Regular from 'components/typography/P1Regular';
import { FEATURE_TOGGLE_KEP_MAINTENANCE } from 'env';
import panic from 'errors/panic';
import { useEffect, useState } from 'react';
import { RevisionListResponse } from 'api/actions/revision-list/revision-list-response';
import RevisionList from 'components/RevisionList';
import openBrainItUrl from 'utils/open-brain-it-url';

/**
 * Parameters of the FinishSigningModal component.
 */
export interface FinishSigningModalProps {
  kepMessageUuid: string;
  message: string | JSX.Element;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal to finish signing revisions with the same kepMessageUuid.
 * Fetches all revisions with the specified kepMessageUuid and displays them.
 */
export default function FinishSigningModal({ kepMessageUuid, message, opened, onClose }: FinishSigningModalProps) {
  const { getAction } = useApi();
  const [fetching, setFetching] = useState(true);
  const [revisions, setRevisions] = useState<RevisionListResponse>([]);
  const [kepSignUrl, setKepSignUrl] = useState<string>('');
  const isKepMaintenanceMode = FEATURE_TOGGLE_KEP_MAINTENANCE;
  const isBusy = !isKepMaintenanceMode && fetching;

  useEffect(() => {
    if (opened && kepMessageUuid) {
      const revisionGetListAction = getAction('RevisionList');

      revisionGetListAction({ query: { filters: { 'kepMessageUuid.eq': kepMessageUuid } } })
        .then((fetchedRevisions) => {
          setRevisions(fetchedRevisions);

          // Get the kepSignUrl from the first revision (they should all have the same one)
          if (fetchedRevisions.length > 0 && fetchedRevisions[0].kepSignUrl) {
            setKepSignUrl(fetchedRevisions[0].kepSignUrl);
          } else {
            panic(new Error('No kepSignUrl found for the revisions'));
            onClose();
          }
        })
        .catch(panic)
        .finally(() => setFetching(false));
    }
  }, [opened, kepMessageUuid]);

  /**
   * Continues the signing process by opening the BrainIT portal.
   */
  const handleContinueSigning = () => {
    if (kepSignUrl) {
      openBrainItUrl(kepSignUrl);
      onClose();
    }
  };

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title="Dokončiť podpisovanie"
      zIndex={1001}
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      closeButtonProps={{ loading: isBusy }}
    >
      <LoadingOverlay visible={isBusy} loaderProps={{ size: 'md' }} zIndex={1000} />
      {isKepMaintenanceMode ? (
        <Stack spacing={24}>
          <KepMaintenanceNotice />
        </Stack>
      ) : (
        <Stack spacing={24}>
          <Stack spacing={16}>
            <P1Regular>{message}</P1Regular>
            <RevisionList revisions={revisions} />
          </Stack>
          {!fetching && (
            <Group position="apart">
              <BrainItMoreInfo />
              <Group position="right">
                <Button variant="subtle-gray" onClick={onClose}>
                  Zrušiť
                </Button>
                <Button variant="primary" onClick={handleContinueSigning} data-autofocus>
                  Vyžiadať podpis
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      )}
    </Modal>
  );
}
